import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Tooltip from "@/v2-ui/tooltip"
import {
  createColumns,
  GROUPING_RANGE_DATES,
  type ColumnFilterOption,
  type GroupingRange,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import LicenseCollectionClaimButton from "@/v2-backoffice-client/license/collections/LicenseCollectionClaimButton"
import { getDaysUntil } from "@/v2-backoffice-client/license/license.utils"
import {
  COLLECTION_FOLLOW_UP_STATE_DISPLAY,
  COLLECTION_STATE_CONTACTED,
  COLLECTION_STATE_ISSUE,
  COLLECTION_STATE_NOT_STARTED,
  COLLECTION_STATE_PAID,
  getCollectionFollowUpState,
  getCollectionOverdueDays,
  getCollectionOverdueVariant,
  type CollectionFollowUpState,
  type CollectionInvoice,
} from "@/v2-backoffice-client/license/collections/license.collections.utils"
import CustomerInvoiceStatusBadge from "@/v2-backoffice-client/customer/billing/CustomerInvoiceStatusBadge"
import {
  formatInvoiceAmount,
  getInvoiceStatusDisplay,
} from "@/v2-backoffice-client/customer/billing/customerBilling.utils"

// Escalation bands matching the Overdue badge's color steps — grouping by
// them splits the book into fresh, nagging and stuck debt.
const GROUPING_RANGE_OVERDUE_DAYS: GroupingRange[] = [
  { label: "Not overdue", test: (v) => !v || Number(v) <= 0 },
  { label: "1 – 14 days", test: (v) => Number(v) <= 14 },
  { label: "15 – 60 days", test: (v) => Number(v) <= 60 },
  { label: "60+ days", test: () => true },
]

// Outstanding-amount bands (NOK in practice) — where the money is.
const GROUPING_RANGE_BALANCE: GroupingRange[] = [
  { label: "Settled", test: (v) => !v || Number(v) <= 0 },
  { label: "< 5k", test: (v) => Number(v) < 5_000 },
  { label: "5k – 25k", test: (v) => Number(v) < 25_000 },
  { label: "25k – 100k", test: (v) => Number(v) < 100_000 },
  { label: "100k +", test: () => true },
]

// The board's row: the query row plus the derived conversation state and a
// display name (customer, account, or a uuid stub). The state machine and
// its display map live in license.collections.utils, shared with the
// customer panel's Collections card.
export type CollectionRow = CollectionInvoice & {
  FollowUpState: CollectionFollowUpState
  DebtorName: string
}

/** The query's rows as the tables show them: the derived state and a debtor name on each. */
export function toCollectionRows(
  invoices: readonly CollectionInvoice[],
): CollectionRow[] {
  return invoices.map((invoice) => ({
    ...invoice,
    FollowUpState: getCollectionFollowUpState(invoice),
    DebtorName:
      invoice.CustomerName ??
      invoice.AccountCompanyName ??
      `Account ${invoice.ExternalInvoiceUuid.slice(0, 8)}`,
  }))
}

/**
 * The owner and Fenerum-status filter options, read off the rows
 * themselves: honest about who actually chases debt and which states exist.
 */
export function collectionFilterOptionsOf(rows: readonly CollectionRow[]) {
  const owners = new Set<string>()
  const statuses = new Set<string>()
  for (const row of rows) {
    if (row.OwnerName) owners.add(row.OwnerName)
    statuses.add(getInvoiceStatusDisplay(row.InvoiceStatus).label)
  }
  return {
    ownerOptions: [
      ...[...owners].sort().map((owner) => ({ label: owner, value: owner })),
      { label: "Unassigned", value: "Unassigned" },
    ],
    statusOptions: [...statuses]
      .sort()
      .map((status) => ({ label: status, value: status })),
  }
}

/**
 * The one column set behind the Collections board and the customer panel's
 * Collections table, so a row reads the same in both places: same badges,
 * same tooltips, same hover explanations. The customer panel hides the
 * debtor (it IS the customer) and leads with the invoice.
 */
export function createCollectionColumns(input: {
  ownerOptions: ColumnFilterOption[]
  statusOptions: ColumnFilterOption[]
  /** The customer panel: every row is this customer's. */
  hidesDebtor?: boolean
}) {
  const { ownerOptions, statusOptions, hidesDebtor = false } = input
  return createColumns<CollectionRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      DebtorName: {
        id: "DebtorName",
        label: "Customer",
        defaultSize: 200,
        defaultHidden: hidesDebtor,
        enableGrouping: true,
        // Push onto the sheet stack — collections → customer and back.
        // Unlinked accounts (no customer yet) can't deep-link.
        to: ({ row }) =>
          row.original.ExternalCustomerId
            ? `>>?v=customer:${row.original.ExternalCustomerId}`
            : undefined,
      },
      InvoiceNumber: {
        id: "InvoiceNumber",
        label: "Invoice",
        defaultSize: 80,
        filter: { type: "text", placeholder: "Invoice number..." },
        getValue: (row) =>
          row.InvoiceNumber != null ? String(row.InvoiceNumber) : "",
      },
      PurchasedLines: {
        id: "PurchasedLines",
        label: "Purchased",
        description:
          "What the chased money is FOR, straight off the invoice's lines — the opening line on the call. Hover for finance's per-line notes and the service period.",
        defaultSize: 180,
        enableGrouping: true,
        filter: { type: "text", placeholder: "Purchased..." },
        getValue: (row) => row.PurchasedLines ?? "",
        renderCell({ row }) {
          const { PurchasedLines, LineSupplements, PeriodFrom, PeriodTo } =
            row.original
          if (!PurchasedLines) return null
          if (!LineSupplements && !PeriodFrom) return PurchasedLines
          return (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <span className="cursor-help">{PurchasedLines}</span>
              </Tooltip.Trigger>
              <Tooltip.Content side="top" className="ui-tooltip-content--panel">
                <div className="flex max-w-xs flex-col gap-1 p-3 text-xs">
                  <p className="font-medium">{PurchasedLines}</p>
                  {PeriodFrom && (
                    <p className="text-muted-foreground">
                      Period: {formatDateString(String(PeriodFrom))} –{" "}
                      {formatDateString(String(PeriodTo))}
                    </p>
                  )}
                  {LineSupplements && (
                    <p className="text-muted-foreground">{LineSupplements}</p>
                  )}
                </div>
              </Tooltip.Content>
            </Tooltip>
          )
        },
      },
      DueDate: {
        id: "DueDate",
        label: "Due",
        defaultSize: 90,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.DueDate
          return value ? formatDateString(String(value)) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.DueDate)
        },
      },
      Overdue: {
        id: "Overdue",
        label: "Overdue",
        description:
          "Days past the due date, freshly derived at render — never stale. Escalates by age.",
        defaultSize: 90,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_OVERDUE_DAYS,
        getValue: (row) => getCollectionOverdueDays(row),
        renderCell({ row }) {
          const overdueDays = getCollectionOverdueDays(row.original)
          if (overdueDays <= 0) return null
          return (
            <Badge variant={getCollectionOverdueVariant(overdueDays)}>
              {overdueDays} days
            </Badge>
          )
        },
      },
      Balance: {
        id: "Balance",
        label: "Outstanding",
        defaultSize: 110,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_BALANCE,
        renderCell({ row }) {
          const { Balance, Currency, OutcomeCode } = row.original
          if (OutcomeCode != null || Balance == null) return null
          return (
            <span className="tabular-nums">
              {formatInvoiceAmount(Balance, Currency)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Balance ?? ""
        },
      },
      InvoiceStatus: {
        id: "InvoiceStatus",
        label: "Fenerum Status",
        description:
          "Fenerum's own invoice state, mirrored hourly: Overdue = past due date, Non-payment = written off / in collection, Paid = settled.",
        defaultSize: 110,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any Fenerum status",
          toolbar: true,
          options: statusOptions,
        },
        // Composes the ONE invoice-status badge (customer/billing), so this
        // board says "Overdue"/"Non-payment"/"Paid" exactly like the Billing
        // tab's invoice list — same labels, same colors, same hover
        // explanation of what the state means and who acts on it.
        getValue: (row) => getInvoiceStatusDisplay(row.InvoiceStatus).label,
        renderCell({ row }) {
          return (
            <CustomerInvoiceStatusBadge status={row.original.InvoiceStatus} />
          )
        },
        exportValue({ row }) {
          return getInvoiceStatusDisplay(row.original.InvoiceStatus).label
        },
      },
      FollowUpState: {
        id: "FollowUpState",
        label: "Follow-up",
        description:
          "Where the payment conversation stands. Paid is derived from the Fenerum mirror — payments land every morning, nobody hand-marks anything.",
        defaultSize: 140,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any follow-up state",
          options: [
            {
              label: COLLECTION_STATE_NOT_STARTED,
              value: COLLECTION_STATE_NOT_STARTED,
              dotVariant: "destructive",
            },
            {
              label: COLLECTION_STATE_CONTACTED,
              value: COLLECTION_STATE_CONTACTED,
              dotVariant: "warning",
            },
            { label: COLLECTION_STATE_ISSUE, value: COLLECTION_STATE_ISSUE },
            {
              label: COLLECTION_STATE_PAID,
              value: COLLECTION_STATE_PAID,
              dotVariant: "success-1",
            },
          ],
        },
        renderCell({ row }) {
          const state = row.original.FollowUpState
          const display = COLLECTION_FOLLOW_UP_STATE_DISPLAY[state]
          return (
            <Badge
              variant={display.variant}
              tooltip={
                <div className="flex max-w-xs flex-col gap-1">
                  <p className="font-medium">{state}</p>
                  <p className="text-muted-foreground">{display.description}</p>
                  {display.action && (
                    <p className="mt-1 text-muted-foreground">
                      {display.action}
                    </p>
                  )}
                </div>
              }
            >
              {state}
            </Badge>
          )
        },
      },
      PromisedPaymentAt: {
        id: "PromisedPaymentAt",
        label: "Promised",
        description:
          "When the customer said they would pay — red once the promise is broken.",
        defaultSize: 100,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const { PromisedPaymentAt, OutcomeCode } = row.original
          if (!PromisedPaymentAt) return null
          const isBroken =
            OutcomeCode == null && getDaysUntil(String(PromisedPaymentAt)) < 0
          return (
            <span className={cn(isBroken && "text-destructive")}>
              {formatDateString(String(PromisedPaymentAt))}
            </span>
          )
        },
        exportValue({ row }) {
          return formatDateString(row.original.PromisedPaymentAt)
        },
      },
      Note: {
        id: "Note",
        label: "Note",
        defaultSize: 220,
        renderCell({ row }) {
          const value = row.original.Note
          if (!value) return null
          return <span className="text-muted-foreground">{value}</span>
        },
      },
      OwnerName: {
        id: "OwnerName",
        label: "Owner",
        description:
          "Who chases this customer's debt — group on it for each CSM's call list. Defaults to the HubSpot company owner; an assignment made here (row menu → Assign Owner) wins. Unassigned rows carry a Claim button — they never hide.",
        defaultSize: 130,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any owner",
          toolbar: true,
          options: ownerOptions,
        },
        getValue: (row) => row.OwnerName ?? "Unassigned",
        renderCell({ row }) {
          const { OwnerName, OwnerSource, CustomerId, CustomerName } =
            row.original
          if (!OwnerName) {
            // An unlinked account has no customer to hang ownership on —
            // linking it (billing accounts board) is the fix.
            if (CustomerId == null) {
              return <span className="text-muted-foreground">—</span>
            }
            return (
              <LicenseCollectionClaimButton
                customerId={CustomerId}
                debtorName={CustomerName ?? "this customer"}
              />
            )
          }
          return (
            <span className="flex items-center gap-1.5 truncate">
              <span className="truncate">{OwnerName}</span>
              {OwnerSource === "Backoffice" && (
                <Icon
                  src="user-pen"
                  size={12}
                  className="shrink-0 text-muted-foreground"
                  aria-label="Assigned in backoffice"
                />
              )}
            </span>
          )
        },
      },
      FollowUpUpdatedByUserName: {
        id: "FollowUpUpdatedByUserName",
        label: "Chased By",
        defaultSize: 130,
        defaultHidden: true,
      },
      FollowUpUpdatedAt: {
        id: "FollowUpUpdatedAt",
        label: "Updated",
        defaultSize: 90,
        defaultHidden: true,
        filter: { type: "date-filter" },
        renderCell({ row }) {
          const value = row.original.FollowUpUpdatedAt
          return value ? formatDateString(String(value)) : null
        },
      },
      Total: {
        id: "Total",
        label: "Invoice Total",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "number-range" },
        renderCell({ row }) {
          const { Total, Currency } = row.original
          if (Total == null) return null
          return (
            <span className="tabular-nums">
              {formatInvoiceAmount(Total, Currency)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Total ?? ""
        },
      },
      InvoiceDate: {
        id: "InvoiceDate",
        label: "Invoiced",
        defaultSize: 90,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.InvoiceDate
          return value ? formatDateString(String(value)) : null
        },
      },
      InvoiceId: {
        id: "InvoiceId",
        label: "Id",
        defaultSize: 50,
        defaultHidden: true,
      },
    },
  })
}
